import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateWebPageSchema, generateAggregateRatingSchema } from '../../utils/seo';

export default function HomePage() {
  // SEO Configuration
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
  
  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      generateLocalBusinessSchema(),
      generateServiceSchema(),
      generateWebPageSchema(
        'Professional Pressure Washing Services in Henderson & Las Vegas',
        'CleanHaus provides expert pressure washing services for residential and commercial properties in Henderson and Las Vegas Metro area.'
      ),
      generateAggregateRatingSchema()
    ]
  };

  useSEO({
    title: 'Henderson Pressure Washing Services | CleanHaus | Las Vegas Metro',
    description: 'Professional pressure washing services in Henderson & Las Vegas. CleanHaus offers residential & commercial cleaning: driveways, patios, roofs, siding. Free quotes, expert results, trusted local service.',
    keywords: 'pressure washing Henderson, power washing Las Vegas, driveway cleaning, roof cleaning, commercial pressure washing',
    canonical: `${siteUrl}/`,
    ogType: 'website',
    ogImage: `${siteUrl}/og-image.jpg`,
    schema: combinedSchema
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState(1);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      id: 1,
      title: 'Driveway Only',
      description: 'Professional pressure washing for your driveway. Remove years of dirt, oil stains, and grime. Our expert cleaning restores the original beauty of your concrete surfaces.',
      icon: 'ri-road-map-line',
      image: 'https://public.readdy.ai/ai/img_res/6771b618774405d9533420f10ad310ba.jpg',
      price: 'Starting at $199',
      note: 'Minimum service',
      features: ['Oil stain removal available', 'Concrete sealing available', 'Flexible scheduling', 'Eco-friendly solutions']
    },
    {
      id: 2,
      title: 'Sidewalk',
      description: 'Keep your sidewalks clean and safe with our thorough pressure washing service. Remove dirt, gum, and stains for improved curb appeal and safety.',
      icon: 'ri-footprint-line',
      image: 'https://readdy.ai/api/search-image?query=spotless%20clean%20residential%20sidewalk%20after%20pressure%20washing%20perfectly%20clean%20concrete%20walkway%20simple%20light%20gray%20background%20suburban%20neighborhood%20setting%20professional%20result&width=800&height=600&seq=sidewalk001&orientation=landscape',
      price: 'Starting at $79',
      features: ['Gum removal', 'Stain elimination', 'Safety improvement', 'Regular maintenance available']
    },
    {
      id: 3,
      title: 'Walkway',
      description: 'Professional walkway cleaning to enhance your home\'s entrance. We carefully clean all surfaces including concrete and pavers for a welcoming pathway.',
      icon: 'ri-walk-line',
      image: 'https://readdy.ai/api/search-image?query=immaculate%20clean%20residential%20walkway%20after%20professional%20pressure%20washing%20bright%20welcoming%20pathway%20simple%20light%20background%20beautiful%20home%20entrance&width=800&height=600&seq=walkway001&orientation=landscape',
      price: 'Starting at $49',
      features: ['Concrete & paver cleaning', 'Quick drying', 'Detail-oriented service', 'Safe for all surfaces']
    },
    {
      id: 4,
      title: 'Front Patio / Porch',
      description: 'Make a great first impression with a spotless porch and patio. We carefully clean all surfaces including wood, concrete, and stone for a welcoming entrance.',
      icon: 'ri-door-open-line',
      image: 'https://readdy.ai/api/search-image?query=immaculate%20clean%20front%20porch%20and%20patio%20after%20professional%20washing%20bright%20welcoming%20entrance%20clean%20surfaces%20simple%20light%20background%20residential%20home%20exterior&width=800&height=600&seq=porch001&orientation=landscape',
      price: 'Starting at $59',
      features: ['Wood-safe cleaning', 'Stone & tile care', 'Quick drying', 'Detail-oriented service']
    },
    {
      id: 5,
      title: 'Pavers',
      description: 'Specialized cleaning for paver surfaces. Restore the vibrant colors and beauty of your pavers. Pricing varies based on size and condition - quoted from photos.',
      icon: 'ri-layout-grid-line',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20clean%20paver%20patio%20after%20professional%20pressure%20washing%20vibrant%20restored%20colors%20inviting%20outdoor%20space%20simple%20light%20background%20luxury%20home&width=800&height=600&seq=pavers001&orientation=landscape',
      price: 'Quoted from photos',
      note: 'Typically higher than concrete',
      features: ['Color restoration', 'Joint sand preservation', 'Gentle cleaning process', 'Photo-based quotes']
    },
    {
      id: 6,
      title: 'Fresh-Front Bundle',
      description: 'Our most popular package! Complete front exterior cleaning including driveway, sidewalk, walkway, and front patio/porch. Perfect for most standard homes.',
      icon: 'ri-home-smile-line',
      image: 'https://readdy.ai/api/search-image?query=stunning%20complete%20front%20home%20exterior%20after%20full%20pressure%20washing%20service%20pristine%20driveway%20clean%20walkways%20beautiful%20patio%20simple%20light%20background%20modern%20residential%20property&width=800&height=600&seq=bundle001&orientation=landscape',
      price: 'Starting at $249',
      badge: 'MOST POPULAR',
      note: 'Oversized homes quoted from photos',
      features: ['Driveway included', 'Sidewalk included', 'Walkway included', 'Front patio/porch included']
    },
    {
      id: 7,
      title: 'Curb Appeal Bundle',
      description: 'Boost your home\'s curb appeal with our essential cleaning package. Includes driveway, sidewalk, and walkway cleaning for a complete front transformation.',
      icon: 'ri-home-gear-line',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20residential%20property%20front%20after%20pressure%20washing%20clean%20driveway%20sidewalk%20and%20walkway%20enhanced%20curb%20appeal%20simple%20light%20background%20suburban%20home&width=800&height=600&seq=bundle002&orientation=landscape',
      price: 'Starting at $229',
      features: ['Driveway included', 'Sidewalk included', 'Walkway included', 'Great value package']
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      location: 'Henderson, NV',
      service: 'Driveway Cleaning',
      rating: 5,
      text: 'CleanHaus did an amazing job on our driveway! It looks brand new. The team was professional, punctual, and the results exceeded our expectations. Highly recommend!',
      date: 'December 2024'
    },
    {
      name: 'Michael Rodriguez',
      location: 'Las Vegas, NV',
      service: 'Pool Deck Restoration',
      rating: 5,
      text: 'Our pool deck was covered in algae and looked terrible. CleanHaus transformed it completely. The difference is night and day. Great service and fair pricing!',
      date: 'November 2024'
    },
    {
      name: 'Jennifer Thompson',
      location: 'Henderson, NV',
      service: 'Complete Property Package',
      rating: 5,
      text: 'We used the complete property package and it was worth every penny. They cleaned our driveway, patio, walkways, and trash bins. Everything looks incredible!',
      date: 'December 2024'
    },
    {
      name: 'David Chen',
      location: 'Las Vegas, NV',
      service: 'Trash Bin Sanitization',
      rating: 5,
      text: 'The trash bin service is a game changer! No more awful smells. They come monthly and keep everything fresh and clean. Very professional service.',
      date: 'November 2024'
    },
    {
      name: 'Amanda Williams',
      location: 'Henderson, NV',
      service: 'Patio Cleaning',
      rating: 5,
      text: 'CleanHaus brought our patio back to life! We can finally enjoy our outdoor space again. The team was friendly and did an excellent job. Will definitely use again!',
      date: 'December 2024'
    },
    {
      name: 'Robert Martinez',
      location: 'Las Vegas, NV',
      service: 'Driveway & Walkway',
      rating: 5,
      text: 'Outstanding service from start to finish. They removed oil stains I thought were permanent. Very impressed with the quality of work and attention to detail.',
      date: 'November 2024'
    }
  ];

  const galleryImages = [
    {
      before: 'https://readdy.ai/api/search-image?query=dirty%20stained%20residential%20concrete%20driveway%20before%20pressure%20washing%20with%20oil%20stains%20dirt%20and%20grime%20visible%20weathered%20appearance%20simple%20background&width=600&height=800&seq=before001&orientation=portrait',
      after: 'https://readdy.ai/api/search-image?query=pristine%20clean%20residential%20concrete%20driveway%20after%20professional%20pressure%20washing%20perfectly%20clean%20bright%20surface%20simple%20light%20background%20modern%20home&width=600&height=800&seq=after001&orientation=portrait',
      location: 'Henderson, NV',
      service: 'Driveway Cleaning',
      date: 'Dec 2024'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=dirty%20algae%20covered%20pool%20deck%20before%20cleaning%20green%20stains%20and%20buildup%20visible%20neglected%20outdoor%20space%20simple%20background&width=600&height=800&seq=before002&orientation=portrait',
      after: 'https://readdy.ai/api/search-image?query=sparkling%20clean%20pool%20deck%20after%20professional%20pressure%20washing%20bright%20clean%20surface%20ready%20for%20use%20simple%20light%20background%20luxury%20backyard&width=600&height=800&seq=after002&orientation=portrait',
      location: 'Las Vegas, NV',
      service: 'Pool Deck Restoration',
      date: 'Dec 2024'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=stained%20dirty%20patio%20pavers%20before%20pressure%20washing%20dark%20discoloration%20and%20grime%20buildup%20visible%20weathered%20outdoor%20space%20simple%20background&width=600&height=800&seq=before003&orientation=portrait',
      after: 'https://readdy.ai/api/search-image?query=beautiful%20clean%20patio%20pavers%20after%20professional%20washing%20vibrant%20colors%20restored%20inviting%20outdoor%20living%20space%20simple%20light%20background&width=600&height=800&seq=after003&orientation=portrait',
      location: 'Henderson, NV',
      service: 'Patio Cleaning',
      date: 'Nov 2024'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=dirty%20concrete%20walkway%20before%20pressure%20washing%20with%20stains%20dirt%20and%20gum%20visible%20neglected%20pathway%20simple%20background%20residential%20setting&width=600&height=800&seq=before004&orientation=portrait',
      after: 'https://readdy.ai/api/search-image?query=immaculate%20clean%20concrete%20walkway%20after%20professional%20pressure%20washing%20perfectly%20clean%20pathway%20welcoming%20entrance%20simple%20light%20background&width=600&height=800&seq=after004&orientation=portrait',
      location: 'Las Vegas, NV',
      service: 'Walkway Cleaning',
      date: 'Nov 2024'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=dirty%20front%20porch%20before%20pressure%20washing%20stained%20concrete%20and%20grimy%20surfaces%20neglected%20home%20entrance%20simple%20background&width=600&height=800&seq=before005&orientation=portrait',
      after: 'https://readdy.ai/api/search-image?query=pristine%20clean%20front%20porch%20after%20professional%20washing%20bright%20welcoming%20entrance%20spotless%20surfaces%20simple%20light%20background&width=600&height=800&seq=after005&orientation=portrait',
      location: 'Henderson, NV',
      service: 'Porch Cleaning',
      date: 'Dec 2024'
    },
    {
      before: 'https://readdy.ai/api/search-image?query=complete%20dirty%20residential%20property%20exterior%20before%20pressure%20washing%20stained%20driveway%20dirty%20walkways%20neglected%20appearance%20simple%20background&width=600&height=800&seq=before006&orientation=portrait',
      after: 'https://readdy.ai/api/search-image?query=stunning%20clean%20complete%20residential%20property%20after%20full%20pressure%20washing%20service%20pristine%20driveway%20clean%20walkways%20beautiful%20transformation%20simple%20light%20background&width=600&height=800&seq=after006&orientation=portrait',
      location: 'Las Vegas, NV',
      service: 'Complete Package',
      date: 'Dec 2024'
    }
  ];

  const [sliderPositions, setSliderPositions] = useState(
    galleryImages.map(() => 50)
  );

  const handleSliderChange = (index: number, value: number) => {
    const newPositions = [...sliderPositions];
    newPositions[index] = value;
    setSliderPositions(newPositions);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/32c4787c82de1936ad76828ef060969d/d733ab063c8647aa31842a2c6eaa589b.png"
                alt="CleanHaus Pressure Washing"
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-white hover:text-[#00D1C7]' : 'text-white hover:text-[#00D1C7]'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-white hover:text-[#00D1C7]' : 'text-white hover:text-[#00D1C7]'
                }`}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-white hover:text-[#00D1C7]' : 'text-white hover:text-[#00D1C7]'
                }`}
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('reviews')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-white hover:text-[#00D1C7]' : 'text-white hover:text-[#00D1C7]'
                }`}
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-white hover:text-[#00D1C7]' : 'text-white hover:text-[#00D1C7]'
                }`}
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-white hover:text-[#00D1C7]' : 'text-white hover:text-[#00D1C7]'
                }`}
              >
                Contact
              </button>
              <button
                onClick={() => scrollToSection('booking')}
                className="bg-[#FF9500] text-black px-6 py-2.5 rounded-lg font-medium hover:bg-[#FFA500] transition-colors whitespace-nowrap cursor-pointer shadow-lg shadow-[#FF9500]/30"
              >
                Book Now
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i
                  className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
                    isScrolled ? 'text-white' : 'text-white'
                  }`}
                ></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-black border-t border-gray-800">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection('home')}
                className="block w-full text-left text-white hover:text-[#00D1C7] font-medium cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="block w-full text-left text-white hover:text-[#00D1C7] font-medium cursor-pointer"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className="block w-full text-left text-white hover:text-[#00D1C7] font-medium cursor-pointer"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('reviews')}
                className="block w-full text-left text-white hover:text-[#00D1C7] font-medium cursor-pointer"
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="block w-full text-left text-white hover:text-[#00D1C7] font-medium cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left text-white hover:text-[#00D1C7] font-medium cursor-pointer"
              >
                Contact
              </button>
              <button
                onClick={() => scrollToSection('booking')}
                className="block w-full bg-[#FF9500] text-black px-6 py-2.5 rounded-lg font-medium hover:bg-[#FFA500] transition-colors cursor-pointer shadow-lg shadow-[#FF9500]/30"
              >
                Book Now
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=stunning%20before%20and%20after%20residential%20driveway%20pressure%20washing%20transformation%20sparkling%20clean%20concrete%20with%20water%20droplets%20glistening%20in%20bright%20sunlight%20professional%20cleaning%20result%20modern%20home%20exterior%20visible%20high%20contrast%20dramatic%20lighting%20beautiful%20outdoor%20space&width=1920&height=1080&seq=hero001&orientation=landscape')`
          }}
        ></div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center">
            {/* Location Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-2 mb-8">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-line text-[#00D1C7] text-lg"></i>
              </div>
              <span className="text-white text-sm font-medium uppercase tracking-wide">
                Henderson, NV | Serving Las Vegas Metro
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
              TRANSFORM YOUR
              <br />
              <span className="text-[#00D1C7]">Outdoor Spaces</span>
              <br />
              <span className="text-4xl md:text-5xl">WITH PROFESSIONAL PRESSURE WASHING</span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto">
              Driveways • Walkways • Patios • Pool Decks • Trash Bins
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button
                onClick={() => scrollToSection('booking')}
                className="bg-[#FF9500] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#FFA500] transition-all shadow-xl shadow-[#FF9500]/40 hover:shadow-2xl flex items-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>BOOK NOW</span>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-xl"></i>
                </div>
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="bg-transparent border-2 border-[#00D1C7] text-[#00D1C7] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00D1C7] hover:text-black transition-all flex items-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>VIEW SERVICES</span>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xl"></i>
                </div>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 flex flex-wrap items-center justify-center gap-6">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-[#FF9500] text-xl"></i>
                  </div>
                  <span className="text-white font-medium text-sm">Licensed &amp; Insured</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-[#FF9500] text-xl"></i>
                  </div>
                  <span className="text-white font-medium text-sm">5-Star Rated</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-[#FF9500] text-xl"></i>
                  </div>
                  <span className="text-white font-medium text-sm">Flexible Appointment Scheduling</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Offer Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#00D1C7] via-[#00b8af] to-[#00a89f] overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-black/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-white">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-[#FF9500] text-black px-4 py-2 rounded-full mb-6 shadow-lg">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-lg"></i>
                </div>
                <span className="font-bold text-sm uppercase tracking-wide">Most Popular</span>
              </div>

              {/* Headline */}
              <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
                Fresh-Front Bundle
              </h2>
              <div className="text-5xl md:text-6xl font-black mb-6 text-[#FF9500]">
                Starting at $249
              </div>

              {/* Includes */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 uppercase tracking-wide">What's Included:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <span className="font-medium">Driveway</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <span className="font-medium">Sidewalk</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <span className="font-medium">Walkway</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-lg"></i>
                    </div>
                    <span className="font-medium">Front Patio/Porch</span>
                  </div>
                </div>
              </div>

              {/* How It Works */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 uppercase tracking-wide">How It Works:</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 font-bold">
                      1
                    </div>
                    <p className="pt-1">Text your address + 2 photos to (702) 332-5893</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 font-bold">
                      2
                    </div>
                    <p className="pt-1">Receive your fast quote within hours</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 font-bold">
                      3
                    </div>
                    <p className="pt-1">$25 deposit locks in your appointment (applied to total)</p>
                  </div>
                </div>
              </div>

              {/* Trust Notes */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-8 border border-white/20">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Photo-based quotes for accuracy</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Sunday/Monday appointments available</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Oversized homes or heavy staining may increase price</span>
                  </li>
                </ul>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="sms:7023325893&body=Hi! I'd like a quote. Here's my address:"
                  className="bg-[#FF9500] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#FFA500] transition-all shadow-xl shadow-black/20 hover:shadow-2xl flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-message-3-line text-xl"></i>
                  </div>
                  <span>Text 702-332-5893 for Fast Quote</span>
                </a>
                <button
                  onClick={() => scrollToSection('services')}
                  className="bg-white text-[#00D1C7] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
                >
                  <span>See What's Included</span>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-down-line text-xl"></i>
                  </div>
                </button>
              </div>
            </div>

            {/* Right Column - Before/After Images */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/30 shadow-2xl">
                <div className="grid grid-cols-2 gap-4">
                  {/* Before Image */}
                  <div className="relative">
                    <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-bold uppercase z-10">
                      Before
                    </div>
                    <div className="rounded-xl overflow-hidden shadow-lg h-64 w-full">
                      <img
                        src="https://readdy.ai/api/search-image?query=dirty%20stained%20residential%20front%20home%20exterior%20before%20pressure%20washing%20complete%20view%20with%20stained%20driveway%20dirty%20walkways%20grimy%20porch%20neglected%20appearance%20weathered%20concrete%20simple%20background&width=400&height=600&seq=featured-before001&orientation=portrait"
                        alt="Before Pressure Washing"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* After Image */}
                  <div className="relative">
                    <div className="absolute top-3 right-3 bg-[#FF9500] text-black px-3 py-1 rounded-full text-xs font-bold uppercase z-10">
                      After
                    </div>
                    <div className="rounded-xl overflow-hidden shadow-lg h-64 w-full">
                      <img
                        src="https://readdy.ai/api/search-image?query=stunning%20clean%20residential%20front%20home%20exterior%20after%20professional%20pressure%20washing%20complete%20view%20pristine%20driveway%20spotless%20walkways%20beautiful%20porch%20sparkling%20clean%20surfaces%20modern%20home%20simple%20light%20background&width=400&height=600&seq=featured-after001&orientation=portrait"
                        alt="After Pressure Washing"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom Text */}
                <div className="mt-6 text-center">
                  <p className="text-white font-bold text-lg">
                    Complete Front Transformation
                  </p>
                  <p className="text-white/80 text-sm mt-1">
                    Most standard homes qualify • Fast turnaround
                  </p>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-[#FF9500] text-black rounded-full w-24 h-24 flex items-center justify-center shadow-2xl border-4 border-white">
                <div className="text-center">
                  <div className="text-2xl font-black leading-none">$249</div>
                  <div className="text-xs font-bold uppercase">Starting</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Our <span className="text-[#00D1C7]">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional pressure washing services tailored to your needs. From single services to complete packages.
            </p>
          </div>

          {/* Featured Bundle Card */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-[#00D1C7] shadow-xl relative overflow-hidden">
              {/* Badge */}
              <div className="absolute top-6 right-6 bg-[#FF9500] text-black px-4 py-2 rounded-full shadow-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                  <span className="font-bold text-xs uppercase tracking-wide">Most Popular</span>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Image */}
                <div className="rounded-xl overflow-hidden shadow-lg h-64 w-full">
                  <img
                    src={services.find(s => s.id === 6)?.image}
                    alt="Fresh-Front Bundle"
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-3xl font-extrabold text-gray-900 mb-2">
                    Fresh-Front Bundle
                  </h3>
                  <p className="text-[#00D1C7] font-bold text-lg mb-4">
                    Best value for complete front transformation
                  </p>
                  <p className="text-gray-700 mb-6">
                    Our most popular package! Complete front exterior cleaning including driveway, sidewalk, walkway, and front patio/porch. Perfect for most standard homes.
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {services.find(s => s.id === 6)?.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-5 h-5 flex items-center justify-center bg-[#00D1C7] text-white rounded-full flex-shrink-0">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        <span className="text-sm text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-4xl font-black text-gray-900">Starting at $249</div>
                      <p className="text-xs text-gray-600 mt-1">Oversized homes quoted from photos</p>
                    </div>
                    <button
                      onClick={() => scrollToSection('booking')}
                      className="bg-[#FF9500] text-black px-6 py-3 rounded-lg font-bold hover:bg-[#FFA500] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Services */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Individual Services &amp; Bundles</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.filter(s => s.id !== 6).map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-200"
              >
                <div className="h-48 w-full overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  {service.badge && (
                    <div className="inline-flex items-center space-x-2 bg-[#FF9500] text-black px-3 py-1 rounded-full mb-3">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs"></i>
                      </div>
                      <span className="font-bold text-xs uppercase">{service.badge}</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                    <div className="w-10 h-10 flex items-center justify-center bg-[#00D1C7]/10 rounded-lg flex-shrink-0">
                      <i className={`${service.icon} text-[#00D1C7] text-2xl`}></i>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  
                  {/* Features */}
                  <div className="space-y-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-4 h-4 flex items-center justify-center bg-[#00D1C7] text-white rounded-full flex-shrink-0">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        <span className="text-xs text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{service.price}</div>
                      {service.note && (
                        <p className="text-xs text-gray-500 mt-1">{service.note}</p>
                      )}
                    </div>
                    <button
                      onClick={() => scrollToSection('booking')}
                      className="bg-[#00D1C7] text-white px-5 py-2 rounded-lg font-medium hover:bg-[#00b8af] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Book
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery Section */}
      <section id="gallery" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              See The <span className="text-[#00D1C7]">Transformation</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real customers. Slide to compare before and after.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryImages.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden shadow-lg">
                <div className="relative h-96 w-full overflow-hidden">
                  {/* After Image (Background) */}
                  <div className="absolute inset-0 w-full h-full">
                    <img
                      src={item.after}
                      alt="After"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Before Image (Overlay with clip-path) */}
                  <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                      clipPath: `inset(0 ${100 - sliderPositions[index]}% 0 0)`
                    }}
                  >
                    <img
                      src={item.before}
                      alt="Before"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Labels */}
                  <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                    Before
                  </div>
                  <div className="absolute top-4 right-4 bg-[#00D1C7] text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                    After
                  </div>

                  {/* Slider */}
                  <div className="absolute inset-0 flex items-center">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={sliderPositions[index]}
                      onChange={(e) => handleSliderChange(index, Number(e.target.value))}
                      className="w-full cursor-pointer opacity-0 z-10 relative"
                      style={{ height: '100%' }}
                    />
                    <div
                      className="absolute top-0 bottom-0 w-1 bg-white shadow-lg pointer-events-none"
                      style={{ left: `${sliderPositions[index]}%` }}
                    >
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-arrow-left-s-line text-gray-700 text-sm"></i>
                          </div>
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-arrow-right-s-line text-gray-700 text-sm"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-1">{item.service}</h3>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <span>{item.location}</span>
                    </div>
                    <span>{item.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              What Our <span className="text-[#00D1C7]">Customers Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. See what Henderson and Las Vegas residents are saying.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200"
              >
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <div key={i} className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-[#FF9500]"></i>
                    </div>
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.text}</p>

                {/* Customer Info */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.location}</p>
                  <p className="text-xs text-[#00D1C7] font-medium mt-1">{testimonial.service}</p>
                  <p className="text-xs text-gray-500 mt-1">{testimonial.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl h-96 w-full">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20pressure%20washing%20technician%20working%20on%20residential%20driveway%20with%20commercial%20equipment%20water%20spray%20action%20modern%20truck%20in%20background%20expert%20service%20Henderson%20Nevada%20suburban%20setting&width=800&height=600&seq=about001&orientation=landscape"
                alt="CleanHaus Professional Service"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
                Why Choose <span className="text-[#00D1C7]">CleanHaus</span>?
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                CleanHaus is Henderson's trusted pressure washing service, serving the entire Las Vegas Metro area. We specialize in residential and commercial exterior cleaning with a focus on quality, reliability, and customer satisfaction.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00D1C7] text-white rounded-lg flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Licensed &amp; Insured</h3>
                    <p className="text-gray-600">Fully licensed and insured for your peace of mind. Professional service you can trust.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00D1C7] text-white rounded-lg flex-shrink-0">
                    <i className="ri-star-fill text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">5-Star Rated Service</h3>
                    <p className="text-gray-600">Consistently rated 5 stars by our customers. Quality work and exceptional customer service.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00D1C7] text-white rounded-lg flex-shrink-0">
                    <i className="ri-leaf-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Eco-Friendly Solutions</h3>
                    <p className="text-gray-600">We use environmentally safe cleaning solutions that are tough on dirt but gentle on your property.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00D1C7] text-white rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Scheduling</h3>
                    <p className="text-gray-600">Sunday and Monday appointments available. We work around your schedule.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-20 bg-gradient-to-br from-[#00D1C7] to-[#00a89f]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Ready To Transform Your Property?
            </h2>
            <p className="text-xl text-white/90">
              Get started with a fast, photo-based quote. Text us your address and 2 photos.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="space-y-8">
              {/* Quick Quote Option */}
              <div className="text-center pb-8 border-b border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Fastest Way: Text Us</h3>
                <p className="text-gray-600 mb-6">
                  Text your address + 2 photos for a fast quote within hours
                </p>
                <a
                  href="sms:7023325893&body=Hi! I'd like a quote. Here's my address:"
                  className="inline-flex items-center space-x-2 bg-[#FF9500] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#FFA500] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-message-3-line text-xl"></i>
                  </div>
                  <span>Text 702-332-5893</span>
                </a>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Or Fill Out This Form</h3>
                <form data-readdy-form id="booking-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D1C7] focus:border-transparent text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D1C7] focus:border-transparent text-sm"
                        placeholder="(702) 555-0123"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D1C7] focus:border-transparent text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                      Property Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D1C7] focus:border-transparent text-sm"
                      placeholder="123 Main St, Henderson, NV 89012"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interested In *
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D1C7] focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="Fresh-Front Bundle">Fresh-Front Bundle (Starting at $249)</option>
                      <option value="Curb Appeal Bundle">Curb Appeal Bundle (Starting at $229)</option>
                      <option value="Driveway Only">Driveway Only (Starting at $199)</option>
                      <option value="Sidewalk">Sidewalk (Starting at $79)</option>
                      <option value="Walkway">Walkway (Starting at $49)</option>
                      <option value="Front Patio/Porch">Front Patio/Porch (Starting at $59)</option>
                      <option value="Pavers">Pavers (Quoted from photos)</option>
                      <option value="Other">Other / Custom Quote</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D1C7] focus:border-transparent text-sm resize-none"
                      placeholder="Tell us about your project, preferred dates, or any special requirements... (500 characters max)"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#FF9500] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#FFA500] transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    Request Quote
                  </button>

                  <p className="text-xs text-gray-600 text-center">
                    By submitting this form, you agree to be contacted about your quote request.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Get In <span className="text-[#00D1C7]">Touch</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Have questions? We're here to help. Reach out to us anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Phone */}
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#00D1C7] text-white rounded-full mx-auto mb-4">
                <i className="ri-phone-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Call or Text</h3>
              <a
                href="tel:7023325893"
                className="text-[#00D1C7] font-bold text-lg hover:underline cursor-pointer"
              >
                (702) 332-5893
              </a>
              <p className="text-sm text-gray-600 mt-2">Available 7 days a week</p>
            </div>

            {/* Email */}
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#00D1C7] text-white rounded-full mx-auto mb-4">
                <i className="ri-mail-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
              <a
                href="mailto:CleanHauspw@gmail.com"
                className="text-[#00D1C7] font-bold text-lg hover:underline cursor-pointer break-all"
              >
                CleanHauspw@gmail.com
              </a>
              <p className="text-sm text-gray-600 mt-2">We'll respond within 24 hours</p>
            </div>

            {/* Location */}
            <div className="bg-gray-50 rounded-xl p-8 text-center hover:shadow-lg transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-[#00D1C7] text-white rounded-full mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Service Area</h3>
              <p className="text-gray-700 font-medium">Henderson, NV</p>
              <p className="text-sm text-gray-600 mt-2">Serving all of Las Vegas Metro</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <img
                src="https://static.readdy.ai/image/32c4787c82de1936ad76828ef060969d/d733ab063c8647aa31842a2c6eaa589b.png"
                alt="CleanHaus"
                className="h-12 w-auto mb-4"
              />
              <p className="text-white/90 mb-4">
                Professional pressure washing services in Henderson and Las Vegas Metro. Transform your property with expert cleaning.
              </p>
              <div className="flex items-center space-x-4">
                <a href="tel:7023325893" className="text-white hover:text-[#FF9500] transition-colors cursor-pointer">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                </a>
                <a href="mailto:CleanHauspw@gmail.com" className="text-white hover:text-[#FF9500] transition-colors cursor-pointer">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('home')} className="text-white/90 hover:text-white transition-colors cursor-pointer">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/90 hover:text-white transition-colors cursor-pointer">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('gallery')} className="text-white/90 hover:text-white transition-colors cursor-pointer">
                    Gallery
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('reviews')} className="text-white/90 hover:text-white transition-colors cursor-pointer">
                    Reviews
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-white/90 hover:text-white transition-colors cursor-pointer">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-white/90">Driveway Cleaning</li>
                <li className="text-white/90">Sidewalk Cleaning</li>
                <li className="text-white/90">Patio Cleaning</li>
                <li className="text-white/90">Pool Deck Cleaning</li>
                <li className="text-white/90">Paver Restoration</li>
                <li className="text-white/90">Complete Packages</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/20 text-center">
            <p className="text-white/80 text-sm">
              © 2025 CleanHaus Pressure Washing. All rights reserved. | Licensed &amp; Insured | 
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors ml-1 cursor-pointer">
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
